import { createFileRoute, notFound } from "@tanstack/react-router";
import { Mail } from "lucide-react";

import { isPublicLocale, PUBLIC_LOCALES } from "@/components/legal-page";
import { SiteFooter } from "@/components/site-footer";
import { TopBar } from "@/components/top-bar";
import { OPERATOR } from "@/lib/operator";
import { SITE_URL } from "@/lib/site-url";

type ContactStrings = {
  metaTitle: string;
  metaDescription: string;
  heading: string;
  subheading: string;
  name: string;
  namePlaceholder: string;
  email: string;
  emailHelp: string;
  subject: string;
  message: string;
  messagePlaceholder: string;
  submit: string;
  sending: string;
  successTitle: string;
  successBody: string;
  errorGeneric: string;
  errorEmail: string;
  errorRate: string;
  orEmail: string;
};

const UI: Record<string, ContactStrings> = {
  en: {
    metaTitle: "Contact — SmsEmpire",
    metaDescription:
      "Get in touch with SmsEmpire support. No account required — useful if you cannot log in.",
    heading: "Contact us",
    subheading:
      "No account needed. If you cannot log in, this is the way to reach us — we reply to the address you give here.",
    name: "Your name (optional)",
    namePlaceholder: "How should we address you?",
    email: "Email",
    emailHelp: "We reply here, so check it is right.",
    subject: "Subject",
    message: "Message",
    messagePlaceholder:
      "Tell us what happened. If it is about a payment or an activation, include the reference or the date — it gets you an answer much faster.",
    submit: "Send message",
    sending: "Sending…",
    successTitle: "Message sent",
    successBody: "We have it. You will get a reply at the address you gave us.",
    errorGeneric: "Could not send the message. Please try again in a moment.",
    errorEmail: "That email address does not look valid.",
    errorRate: "Too many messages from this connection. Please try again later.",
    orEmail: "You can also write directly to",
  },
  es: {
    metaTitle: "Contacto — SmsEmpire",
    metaDescription:
      "Contacta con el soporte de SmsEmpire. No hace falta cuenta — útil si no puedes iniciar sesión.",
    heading: "Contacta con nosotros",
    subheading:
      "No hace falta cuenta. Si no puedes iniciar sesión, esta es la vía para localizarnos: respondemos a la dirección que indiques aquí.",
    name: "Tu nombre (opcional)",
    namePlaceholder: "¿Cómo nos dirigimos a ti?",
    email: "Correo electrónico",
    emailHelp: "Te respondemos aquí, así que revisa que sea correcto.",
    subject: "Asunto",
    message: "Mensaje",
    messagePlaceholder:
      "Cuéntanos qué ha pasado. Si es sobre un pago o una activación, incluye la referencia o la fecha: acelera mucho la respuesta.",
    submit: "Enviar mensaje",
    sending: "Enviando…",
    successTitle: "Mensaje enviado",
    successBody: "Lo hemos recibido. Te responderemos a la dirección que nos has dado.",
    errorGeneric: "No se pudo enviar el mensaje. Inténtalo de nuevo en un momento.",
    errorEmail: "Esa dirección de correo no parece válida.",
    errorRate: "Demasiados mensajes desde esta conexión. Inténtalo más tarde.",
    orEmail: "También puedes escribir directamente a",
  },
  fr: {
    metaTitle: "Contact — SmsEmpire",
    metaDescription:
      "Contactez l’assistance SmsEmpire. Aucun compte requis — utile si vous n’arrivez pas à vous connecter.",
    heading: "Contactez-nous",
    subheading:
      "Aucun compte requis. Si vous n’arrivez pas à vous connecter, c’est par ici : nous répondons à l’adresse indiquée ci-dessous.",
    name: "Votre nom (facultatif)",
    namePlaceholder: "Comment devons-nous vous appeler ?",
    email: "Adresse e-mail",
    emailHelp: "Nous répondons ici, vérifiez donc qu’elle est correcte.",
    subject: "Objet",
    message: "Message",
    messagePlaceholder:
      "Dites-nous ce qui s’est passé. S’il s’agit d’un paiement ou d’une activation, indiquez la référence ou la date : cela accélère beaucoup la réponse.",
    submit: "Envoyer le message",
    sending: "Envoi…",
    successTitle: "Message envoyé",
    successBody: "Bien reçu. Nous vous répondrons à l’adresse indiquée.",
    errorGeneric: "Impossible d’envoyer le message. Réessayez dans un instant.",
    errorEmail: "Cette adresse e-mail ne semble pas valide.",
    errorRate: "Trop de messages depuis cette connexion. Réessayez plus tard.",
    orEmail: "Vous pouvez aussi écrire directement à",
  },
  de: {
    metaTitle: "Kontakt — SmsEmpire",
    metaDescription:
      "Kontaktieren Sie den SmsEmpire-Support. Kein Konto nötig — hilfreich, wenn Sie sich nicht anmelden können.",
    heading: "Kontakt",
    subheading:
      "Kein Konto nötig. Wenn Sie sich nicht anmelden können, erreichen Sie uns hier: Wir antworten an die unten angegebene Adresse.",
    name: "Ihr Name (optional)",
    namePlaceholder: "Wie sollen wir Sie ansprechen?",
    email: "E-Mail-Adresse",
    emailHelp: "Wir antworten hierhin — bitte prüfen Sie die Adresse.",
    subject: "Betreff",
    message: "Nachricht",
    messagePlaceholder:
      "Schildern Sie, was passiert ist. Bei Zahlungen oder Aktivierungen bitte Referenz oder Datum angeben: das beschleunigt die Antwort erheblich.",
    submit: "Nachricht senden",
    sending: "Wird gesendet…",
    successTitle: "Nachricht gesendet",
    successBody: "Angekommen. Wir antworten an die angegebene Adresse.",
    errorGeneric: "Die Nachricht konnte nicht gesendet werden. Bitte gleich noch einmal versuchen.",
    errorEmail: "Diese E-Mail-Adresse sieht nicht gültig aus.",
    errorRate: "Zu viele Nachrichten von dieser Verbindung. Bitte später erneut versuchen.",
    orEmail: "Sie können auch direkt schreiben an",
  },
  it: {
    metaTitle: "Contatti — SmsEmpire",
    metaDescription:
      "Contatta l’assistenza di SmsEmpire. Non serve un account — utile se non riesci ad accedere.",
    heading: "Contattaci",
    subheading:
      "Non serve un account. Se non riesci ad accedere, questa è la via per raggiungerci: rispondiamo all’indirizzo che indichi qui.",
    name: "Il tuo nome (facoltativo)",
    namePlaceholder: "Come possiamo chiamarti?",
    email: "Indirizzo email",
    emailHelp: "Rispondiamo qui, quindi controlla che sia corretto.",
    subject: "Oggetto",
    message: "Messaggio",
    messagePlaceholder:
      "Raccontaci cosa è successo. Se riguarda un pagamento o un’attivazione, includi il riferimento o la data: velocizza molto la risposta.",
    submit: "Invia messaggio",
    sending: "Invio…",
    successTitle: "Messaggio inviato",
    successBody: "L’abbiamo ricevuto. Ti risponderemo all’indirizzo che ci hai dato.",
    errorGeneric: "Impossibile inviare il messaggio. Riprova tra un momento.",
    errorEmail: "Questo indirizzo email non sembra valido.",
    errorRate: "Troppi messaggi da questa connessione. Riprova più tardi.",
    orEmail: "Puoi anche scrivere direttamente a",
  },
  pt: {
    metaTitle: "Contacto — SmsEmpire",
    metaDescription:
      "Contacte o apoio da SmsEmpire. Não é preciso conta — útil se não conseguir iniciar sessão.",
    heading: "Fale connosco",
    subheading:
      "Não é preciso conta. Se não conseguir iniciar sessão, é por aqui que nos encontra: respondemos para o endereço que indicar.",
    name: "O seu nome (opcional)",
    namePlaceholder: "Como o tratamos?",
    email: "Endereço de email",
    emailHelp: "Respondemos para aqui, por isso confirme que está correto.",
    subject: "Assunto",
    message: "Mensagem",
    messagePlaceholder:
      "Diga-nos o que aconteceu. Se for sobre um pagamento ou uma ativação, inclua a referência ou a data: acelera muito a resposta.",
    submit: "Enviar mensagem",
    sending: "A enviar…",
    successTitle: "Mensagem enviada",
    successBody: "Recebemos. Vamos responder para o endereço que nos deu.",
    errorGeneric: "Não foi possível enviar a mensagem. Tente novamente daqui a pouco.",
    errorEmail: "Esse endereço de email não parece válido.",
    errorRate: "Demasiadas mensagens desta ligação. Tente mais tarde.",
    orEmail: "Também pode escrever diretamente para",
  },
  ru: {
    metaTitle: "Контакты — SmsEmpire",
    metaDescription:
      "Свяжитесь с поддержкой SmsEmpire. Аккаунт не нужен — пригодится, если не удаётся войти.",
    heading: "Связаться с нами",
    subheading:
      "Аккаунт не нужен. Если не получается войти, пишите сюда: мы ответим на указанный вами адрес.",
    name: "Ваше имя (необязательно)",
    namePlaceholder: "Как к вам обращаться?",
    email: "Адрес электронной почты",
    emailHelp: "Мы отвечаем сюда — проверьте, что адрес верный.",
    subject: "Тема",
    message: "Сообщение",
    messagePlaceholder:
      "Расскажите, что произошло. Если это про оплату или активацию, укажите номер или дату: это заметно ускорит ответ.",
    submit: "Отправить сообщение",
    sending: "Отправка…",
    successTitle: "Сообщение отправлено",
    successBody: "Получили. Ответим на указанный вами адрес.",
    errorGeneric: "Не удалось отправить сообщение. Попробуйте ещё раз через минуту.",
    errorEmail: "Этот адрес электронной почты выглядит недействительным.",
    errorRate: "Слишком много сообщений с этого подключения. Попробуйте позже.",
    orEmail: "Также можно написать напрямую на",
  },
  zh: {
    metaTitle: "联系我们 — SmsEmpire",
    metaDescription: "联系 SmsEmpire 客服。无需账户——在你无法登录时特别有用。",
    heading: "联系我们",
    subheading: "无需账户。如果你无法登录，可以从这里找到我们：我们会回复你在此填写的邮箱。",
    name: "你的称呼（可选）",
    namePlaceholder: "我们该怎么称呼你？",
    email: "电子邮箱",
    emailHelp: "我们会回复到这里，请确认填写正确。",
    subject: "主题",
    message: "留言",
    messagePlaceholder:
      "告诉我们发生了什么。如果与付款或激活有关，请附上单号或日期：这能大大加快处理速度。",
    submit: "发送留言",
    sending: "发送中…",
    successTitle: "留言已发送",
    successBody: "已收到。我们会回复到你提供的邮箱。",
    errorGeneric: "留言发送失败，请稍后再试。",
    errorEmail: "该邮箱地址似乎无效。",
    errorRate: "来自此连接的留言过多，请稍后再试。",
    orEmail: "你也可以直接写信到",
  },
};

function stringsFor(lang: string): ContactStrings {
  return UI[lang] ?? UI.en;
}

export const Route = createFileRoute("/$lang/contact")({
  beforeLoad: ({ params }) => {
    if (!isPublicLocale(params.lang)) throw notFound();
  },
  head: ({ params }) => {
    const ui = stringsFor(params.lang);
    const canonical = `${SITE_URL}/${params.lang}/contact`;
    return {
      meta: [
        { title: ui.metaTitle },
        { name: "description", content: ui.metaDescription },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: ui.metaTitle },
        { property: "og:description", content: ui.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/contact` },
        ...PUBLIC_LOCALES.map((l) => ({
          rel: "alternate",
          hrefLang: l,
          href: `${SITE_URL}/${l}/contact`,
        })),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: ui.metaTitle,
            url: canonical,
            inLanguage: params.lang,
          }),
        },
      ],
    };
  },
  component: ContactPage,
});

function ContactPage() {
  const { lang } = Route.useParams();
  const ui = stringsFor(lang);

  // ── Public contact form is temporarily disabled ──────────────────────────
  // Only logged-in users with a verified e-mail can open tickets (/tickets).
  const disabledNotice: Record<string, { heading: string; body: string }> = {
    en: {
      heading: "Contact form temporarily unavailable",
      body: "Our public contact form is currently closed. If you have an account with a verified e-mail address, please log in and use the Tickets section to reach us.",
    },
    es: {
      heading: "Formulario de contacto temporalmente no disponible",
      body: "El formulario de contacto público está cerrado por el momento. Si tienes una cuenta con correo verificado, inicia sesión y usa la sección de Tickets para contactarnos.",
    },
    fr: {
      heading: "Formulaire de contact temporairement indisponible",
      body: "Notre formulaire de contact public est actuellement fermé. Si vous avez un compte avec une adresse e-mail vérifiée, connectez-vous et utilisez la section Tickets.",
    },
    de: {
      heading: "Kontaktformular vorübergehend nicht verfügbar",
      body: "Unser öffentliches Kontaktformular ist derzeit geschlossen. Wenn Sie ein Konto mit verifizierter E-Mail-Adresse haben, melden Sie sich an und nutzen Sie den Bereich Tickets.",
    },
    it: {
      heading: "Modulo di contatto temporaneamente non disponibile",
      body: "Il nostro modulo di contatto pubblico è attualmente chiuso. Se hai un account con un indirizzo e-mail verificato, accedi e usa la sezione Ticket.",
    },
    pt: {
      heading: "Formulário de contato temporariamente indisponível",
      body: "Nosso formulário de contato público está fechado no momento. Se tiver uma conta com e-mail verificado, faça login e use a seção de Tickets.",
    },
    ru: {
      heading: "Форма обратной связи временно недоступна",
      body: "Наша публичная форма обратной связи временно закрыта. Если у вас есть аккаунт с подтверждённым e-mail, войдите в систему и откройте тикет в разделе Tickets.",
    },
    zh: {
      heading: "联系表单暂时不可用",
      body: "我们的公开联系表单目前已关闭。如果您有已验证邮箱的账户，请登录并使用工单（Tickets）功能联系我们。",
    },
  };
  const notice = disabledNotice[lang] ?? disabledNotice.en;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopBar />

      <main className="mx-auto w-full max-w-2xl flex-1 px-6 py-12">
        <header>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">{ui.heading}</h1>
        </header>

        <div className="mt-10 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-8 text-center">
          <Mail className="mx-auto h-10 w-10 text-amber-400" aria-hidden />
          <h2 className="mt-4 text-xl font-semibold text-foreground">{notice.heading}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{notice.body}</p>
        </div>

        {OPERATOR.supportEmail && (
          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" aria-hidden />
            {ui.orEmail}{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`} className="text-primary hover:underline">
              {OPERATOR.supportEmail}
            </a>
          </p>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
